import { ownAmount, type PeriodSection, type PeriodTemplateItem, type PlanShare } from '@fin/shared';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { CategoryPicker, SharesEditor, WalletPicker, planShareDrafts, sharesOf, type ShareDraft } from '../transactions/public.ts';

import { Button, ConfirmButton, Icon, Input, Modal, NumberInput, Segmented, Select, Tile, cx, toast } from '../../components/ui.tsx';
import { authStore, del, patch, post } from '../../lib/api.ts';
import { categoryLabel, useCategories, useContacts, useDebts, useGoals, useRates, useWallets } from '../../lib/data.ts';
import { money, rub } from '../../lib/format.ts';
import { categoryColor, categoryIcon, kindTint, walletIcon } from '../../lib/icons.tsx';

import { invalidateMoney, useQuery } from '../../lib/query.ts';

import { type Line, emptyLine, sectionTitles, type LineKind, lineKindOf, lineKinds } from './model.tsx';

export function CurrencySelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const cur = useQuery<{ code: string }[]>('/currencies');
  return (
    <Select aria-label="Валюта" value={value} onChange={(e) => onChange(e.target.value)} className="cur">
      {(cur.data ?? [{ code: value }]).map((c) => <option key={c.code}>{c.code}</option>)}
    </Select>
  );
}

export function SelectRow({ label, value, options, placeholder, tile, onChange }: {
  label: string; value: number | null; options: { id: number; name: string }[]; placeholder: string; tile: ReactNode; onChange: (v: number | null) => void;
}) {
  const chosen = options.find((o) => o.id === value);
  return (
    <label className="qa-row qa-row-select">
      {tile}
      <span className="qa-row-text">
        <span className="qa-row-label">{label}</span>
        <span className={cx('qa-row-value', !chosen && 'placeholder')}>{chosen?.name ?? placeholder}</span>
      </span>
      <span className="qa-row-aside"><Icon name="down" size={18} /></span>
      <select aria-label={label} value={value ?? ''} onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}>
        <option value="">{placeholder}</option>
        {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
      </select>
    </label>
  );
}

/** who of a period line's people already returned their part; «Оплатить за него» before the line is paid */
function LineShareStatus({ shares, currency }: { shares: PlanShare[]; currency: string }) {
  const [busy, setBusy] = useState<number | null>(null);
  const tracked = shares.filter((s) => s.debtId);
  if (!tracked.length) return null;
  const cover = async (s: PlanShare) => {
    setBusy(s.debtId!);
    try {
      await post(`/shares/${s.debtId}/cover`);
      invalidateMoney();
      toast(`${money(s.remaining ?? 0, currency)} за ${s.contactName || 'человека'} — теперь ваша часть`);
    } catch (e) { toast((e as Error).message, 'error'); } finally { setBusy(null); }
  };
  return (
    <div className="shares">
      {tracked.map((s) => (
        <div key={s.debtId} className="share-line">
          <span className="grow">{s.contactName || '—'}<span className="muted small"> · {(s.remaining ?? 0) <= 0.005 ? ((s.covered ?? 0) > 0.005 ? 'оплачено вами' : 'вернул') : (s.paid ?? 0) > 0.005 ? `осталось ${money(s.remaining ?? 0, currency)}` : 'ждёт возврата'}</span></span>
          {(s.remaining ?? 0) > 0.005 ? (
            <ConfirmButton onConfirm={() => cover(s)} confirmText={`Списать ${money(s.remaining ?? 0, currency)} на себя?`}>{busy === s.debtId ? '…' : 'Оплатить за него'}</ConfirmButton>
          ) : null}
        </div>
      ))}
    </div>
  );
}

export function LineForm({ initial, section, extra, onSubmit, onCancel, onDelete }: {
  initial: Line; section: PeriodSection; extra?: ReactNode;
  onSubmit: (d: Partial<Line>) => Promise<void>; onCancel: () => void; onDelete?: () => Promise<void>;
}) {
  const cats = useCategories();
  const wallets = useWallets();
  const debts = useDebts();
  const goals = useGoals();
  const rates = useRates();
  const [d, setD] = useState<Line>(initial);
  const [kind, setKind] = useState<LineKind>(lineKindOf(initial));
  // a transfer is planned in the sending currency (the main account's): what arrives in another currency
  // is entered when it is paid. A line kept in the receiving currency is shown converted, the old sum noted.
  const [was, setWas] = useState<{ amount: number; currency: string } | null>(null);
  const sendsIn = rates.base;
  const legacy = section !== 'foreign' && lineKindOf(initial) === 'transfer' && initial.currency !== sendsIn;
  useEffect(() => {
    if (!legacy || was) return;
    const v = rates.toBase(initial.amount, initial.currency);
    if (v === null) return;
    setWas({ amount: initial.amount, currency: initial.currency });
    setD((x) => ({ ...x, amount: Math.round(v), currency: sendsIn }));
  }, [legacy, was, rates.toBase, initial.amount, initial.currency, sendsIn]);
  const [busy, setBusy] = useState(false);
  const [picker, setPicker] = useState<'category' | 'wallet' | null>(null);
  const contacts = useContacts();
  // «Разделить с другими»: a payment other people chip in for; the period counts only the owner's part
  const [shareRows, setShareRows] = useState<ShareDraft[]>(() => planShareDrafts(initial.shares));
  // a line takes its name from what was picked, until a name of its own is typed
  const autoName = useRef('');
  const set = (p: Partial<Line>) => setD((x) => ({ ...x, ...p }));
  const named = (name: string) => {
    if (d.name.trim() && d.name !== autoName.current) return {};
    autoName.current = name;
    return { name };
  };
  const isNew = !initial.id;
  const category = cats.data?.find((c) => c.id === d.categoryId);
  const wallet = wallets.data?.find((w) => w.id === d.walletId);
  const k: LineKind = section === 'mandatory' ? kind : 'purchase';
  const shareable = section === 'mandatory' && (k === 'purchase' || k === 'transfer');
  return (
    <form className="line-form" onSubmit={async (e) => {
      e.preventDefault();
      if (!d.name.trim()) return;
      if (k === 'transfer' && !d.walletId) { toast('Выберите счёт, на который переводить', 'error'); return; }
      setBusy(true);
      try {
        // only the fields of the chosen type are kept, so a line is never two things at once
        await onSubmit({
          name: d.name.trim(), amount: d.amount, currency: k === 'transfer' && section !== 'foreign' ? sendsIn : d.currency, highlight: k === 'debt' ? d.highlight : false,
          categoryId: k === 'purchase' ? d.categoryId : null,
          walletId: k === 'transfer' ? d.walletId : null,
          debtId: k === 'debt' ? d.debtId : null,
          goalId: k === 'goal' ? d.goalId : null,
          ...(section !== 'foreign' ? { shares: shareable ? sharesOf(shareRows).map(({ debtId, contactId, contactName, amount }) => ({ debtId: debtId ?? undefined, contactId: contactId ?? null, contactName: contactName ?? '', amount })) : [] } : {}),
          ...(d.included !== undefined ? { included: d.included } : {}),
        });
      } catch { /* toast shown */ } finally { setBusy(false); }
    }}>
      {section === 'mandatory' ? <Segmented wide label="Тип строки" value={kind} onChange={setKind} options={lineKinds} /> : null}
      <div className="line-form-row">
        <Input value={d.name} onChange={(e) => set({ name: e.target.value })} placeholder={section === 'optional' ? 'Например, Продукты' : 'Название'} aria-label="Название" autoFocus />
        <NumberInput value={d.amount || null} onChange={(v) => set({ amount: v ?? 0 })} placeholder="Сумма" aria-label="Сумма" />
        {section === 'foreign' ? <CurrencySelect value={d.currency} onChange={(v) => set({ currency: v })} /> : null}
      </div>
      {section !== 'foreign' ? (
        <div className="qa-rows">
          {k === 'purchase' ? (
            <button type="button" className="qa-row" onClick={() => setPicker('category')}>
              {category ? <Tile icon={categoryIcon(category)} color={categoryColor(category)} image={category.iconImage} /> : <Tile icon="folder" color={kindTint.expense} />}
              <span className="qa-row-text">
                <span className="qa-row-label">Категория</span>
                <span className={cx('qa-row-value', !category && 'placeholder')}>{category ? categoryLabel(category, cats.data ?? []) : 'По названию строки'}</span>
              </span>
              <span className="qa-row-aside"><Icon name="right" size={18} /></span>
            </button>
          ) : k === 'transfer' ? (
            <button type="button" className="qa-row" onClick={() => setPicker('wallet')}>
              {wallet ? <Tile icon={walletIcon(wallet)} color={wallet.color} /> : <Tile icon="transfer" color={kindTint.transfer} />}
              <span className="qa-row-text">
                <span className="qa-row-label">На счёт</span>
                <span className={cx('qa-row-value', !wallet && 'placeholder')}>{wallet?.name ?? 'Выбрать'}</span>
              </span>
              <span className="qa-row-aside">{wallet ? <span className="money">{money(wallet.balance, wallet.currency)}</span> : null}<Icon name="right" size={18} /></span>
            </button>
          ) : k === 'debt' ? (
            <SelectRow label="Какой долг гасится" value={d.debtId} placeholder="Выбрать" tile={<Tile icon="hand" color={kindTint.repayment} />}
              options={(debts.data ?? []).filter((x) => x.direction === 'borrowed' && (!x.closedAt || x.id === d.debtId)).map((x) => ({ id: x.id, name: x.title }))}
              onChange={(v) => { const x = debts.data?.find((y) => y.id === v); set({ debtId: v, ...(x ? named(x.title) : {}) }); }} />
          ) : (
            <SelectRow label="Откладывать в цель" value={d.goalId} placeholder="Выбрать" tile={<Tile icon="piggy" color="#5EE6A8" />}
              options={(goals.data ?? []).map((x) => ({ id: x.id, name: x.name }))}
              onChange={(v) => { const x = goals.data?.find((y) => y.id === v); set({ goalId: v, ...(x ? named(x.name) : {}) }); }} />
          )}
        </div>
      ) : null}
      {k === 'transfer' && section !== 'foreign' && (wallet && wallet.currency !== sendsIn || was) ? (
        <p className="line-form-note muted small">
          Сумма — в {sendsIn}, столько спишется с основного счёта{wallet && wallet.currency !== sendsIn ? <>; сколько придёт в {wallet.currency}, укажете при оплате</> : null}.
          {was ? <> Было <span className="money">{money(was.amount, was.currency)}</span>, пересчитано по курсу последних переводов.</> : null}
        </p>
      ) : null}
      {shareable ? (shareRows.length ? (
        <div className="shares-panel">
          <span className="qa-lines-label">Доли других</span>
          <SharesEditor value={shareRows} onChange={setShareRows} contacts={contacts.data ?? []} total={d.amount || null} currency={k === 'transfer' ? sendsIn : d.currency}
            settled={new Map((initial.shares ?? []).filter((s) => s.debtId).map((s) => [s.debtId!, (s.paid ?? 0) + (s.covered ?? 0)]))} />
          <LineShareStatus shares={initial.shares ?? []} currency={d.currency} />
        </div>
      ) : (
        <Button size="sm" className="share-add" onClick={() => setShareRows([{ key: crypto.randomUUID(), contactId: '', contactName: '', amount: null }])}>
          <Icon name="plus" size={16} /> Разделить с другими
        </Button>
      )) : null}
      {extra}
      <div className="line-form-row line-form-foot">
        {d.included !== undefined ? <label className="check"><input type="checkbox" checked={d.included} onChange={(e) => set({ included: e.target.checked })} /> Учитывать</label> : null}
        <span className="grow" />
        {onDelete ? <ConfirmButton onConfirm={onDelete}>Удалить</ConfirmButton> : null}
        <Button size="sm" onClick={onCancel}>Отмена</Button>
        <Button size="sm" type="submit" variant="primary" busy={busy}>{isNew ? 'Добавить' : 'Сохранить'}</Button>
      </div>
      {picker === 'category' ? (
        <CategoryPicker kind="expense" value={d.categoryId} onClose={() => setPicker(null)}
          allLabel="По названию строки" onAll={() => { set({ categoryId: null }); setPicker(null); }}
          onPick={(id) => { const c = cats.data?.find((x) => x.id === id); set({ categoryId: id, ...(c ? named(c.name) : {}) }); setPicker(null); }} />
      ) : null}
      {picker === 'wallet' ? (
        <WalletPicker title="Куда переводить" value={d.walletId} onClose={() => setPicker(null)}
          onPick={(id) => { const w = wallets.data?.find((x) => x.id === id); set({ walletId: id, ...(w ? named(`Перевод на ${w.name}`) : {}) }); setPicker(null); }} />
      ) : null}
    </form>
  );
}

export function LineModal({ section, initial, extra, onClose, onSubmit, onDelete }: {
  section: PeriodSection; initial?: Line; extra?: ReactNode; onClose: () => void;
  onSubmit: (d: Partial<Line>) => Promise<void>; onDelete?: () => Promise<void>;
}) {
  return (
    <Modal className="modal-form" title={initial ? initial.name || 'Строка' : `Новая строка: ${sectionTitles[section].toLowerCase()}`} onClose={onClose}>
      <LineForm initial={initial ?? { id: 0, section, name: '', amount: 0, currency: section === 'foreign' ? 'KZT' : 'RUB', ...emptyLine }}
        section={section} extra={extra} onCancel={onClose} onSubmit={async (d) => { await onSubmit(d); onClose(); }}
        onDelete={onDelete ? async () => { await onDelete(); onClose(); } : undefined} />
    </Modal>
  );
}

export function ReorderLines({ items, onSave, onDone }: {
  items: { id: number; name: string; amount: number; currency: string }[]; onSave: (ids: number[]) => Promise<void>; onDone: () => void;
}) {
  const [order, setOrder] = useState(() => items.map((i) => i.id));
  const [dragId, setDragId] = useState<number | null>(null);
  const [dy, setDy] = useState(0);
  const drag = useRef<{ id: number; pointer: number; startY: number; h: number; moved: boolean } | null>(null);
  const byId = new Map(items.map((i) => [i.id, i]));
  const shown = [...order.filter((id) => byId.has(id)), ...items.filter((i) => !order.includes(i.id)).map((i) => i.id)];
  const save = (next: number[]) => { void onSave(next).catch((e) => toast((e as Error).message, 'error')); };
  const move = (id: number, by: number) => {
    const i = shown.indexOf(id), j = i + by;
    if (j < 0 || j >= shown.length) return;
    const next = [...shown];
    [next[i], next[j]] = [next[j], next[i]];
    setOrder(next);
    save(next);
  };
  const down = (e: React.PointerEvent, id: number) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();
    const row = (e.currentTarget as HTMLElement).closest('li');
    drag.current = { id, pointer: e.pointerId, startY: e.clientY, h: row?.offsetHeight || 56, moved: false };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* the pointer is already gone */ }
    setDragId(id);
  };
  const moveDrag = (e: React.PointerEvent) => {
    const g = drag.current;
    if (!g || g.pointer !== e.pointerId) return;
    let off = e.clientY - g.startY;
    const i = shown.indexOf(g.id);
    // past half a row the line trades places with its neighbour; the offset continues from its new place
    if (off > g.h / 2 && i < shown.length - 1) {
      const next = [...shown]; [next[i], next[i + 1]] = [next[i + 1], next[i]]; setOrder(next);
      g.startY += g.h; off -= g.h; g.moved = true;
    } else if (off < -g.h / 2 && i > 0) {
      const next = [...shown]; [next[i], next[i - 1]] = [next[i - 1], next[i]]; setOrder(next);
      g.startY -= g.h; off += g.h; g.moved = true;
    }
    setDy(off);
  };
  const up = (e: React.PointerEvent) => {
    const g = drag.current;
    if (!g || g.pointer !== e.pointerId) return;
    drag.current = null;
    setDragId(null);
    setDy(0);
    if (g.moved) save(shown);
  };
  return (
    <>
      <ul className="plan-lines reorder-lines" aria-label="Порядок строк">
        {shown.map((id, i) => {
          const it = byId.get(id)!;
          return (
            <li key={id} className={cx('plan-line reorder-line', dragId === id && 'is-dragging')}
              style={dragId === id ? { transform: `translateY(${dy}px)` } : undefined}>
              <span className="reorder-handle" aria-hidden onPointerDown={(e) => down(e, id)} onPointerMove={moveDrag} onPointerUp={up} onPointerCancel={up}>
                <Icon name="grip" size={20} />
              </span>
              <span className="plan-name">{it.name}</span>
              <span className="plan-amount"><span className="money">{money(it.amount, it.currency)}</span></span>
              <span className="reorder-arrows">
                <button type="button" className="icon-btn" aria-label={`Выше: ${it.name}`} disabled={i === 0} onClick={() => move(id, -1)}><Icon name="up" size={18} /></button>
                <button type="button" className="icon-btn" aria-label={`Ниже: ${it.name}`} disabled={i === shown.length - 1} onClick={() => move(id, 1)}><Icon name="down" size={18} /></button>
              </span>
            </li>
          );
        })}
      </ul>
      <div className="plan-tools plan-tools-end">
        <Button size="sm" variant="primary" onClick={onDone}>Готово</Button>
      </div>
    </>
  );
}

export function TemplatesModal({ onClose }: { onClose: () => void }) {
  const user = authStore.get().user;
  const days = user?.periodStartDays ?? [10, 25];
  const [slot, setSlot] = useState('0');
  const [editing, setEditing] = useState<number | string | null>(null);
  const [ordering, setOrdering] = useState<PeriodSection | null>(null);
  const q = useQuery<PeriodTemplateItem[]>('/period-templates');
  const items = (q.data ?? []).filter((t) => t.slot === Number(slot));
  const reload = () => q.reload();
  const wrap = async (p: Promise<unknown>) => { try { await p; } catch (e) { toast((e as Error).message, 'error'); throw e; } };
  return (
    <Modal wide title="Шаблоны периодов" onClose={onClose}>
      <p className="muted">Строки шаблона копируются в каждый новый период, начинающийся в этот день месяца.</p>
      <Segmented label="Шаблон" value={slot} onChange={setSlot} options={days.map((d, i) => ({ value: String(i), label: `С ${d}-го` }))} />
      {!q.data ? <div className="skeleton sk-rows" /> : (['mandatory', 'optional'] as PeriodSection[]).map((section) => {
        const rows = items.filter((i) => i.section === section);
        return (
          <section key={section} className="tpl-section">
            <div className="section-head"><h2>{sectionTitles[section]}</h2><strong className="money">{rub(rows.reduce((s, i) => s + ownAmount(i), 0))}</strong></div>
            {ordering === section ? (
              <ReorderLines items={rows} onDone={() => setOrdering(null)}
                onSave={async (ids) => { await post('/period-templates/reorder', { ids }); reload(); }} />
            ) : <>
            <ul className="plan-lines">
              {rows.map((it) => (
                <li key={it.id} className="plan-line tpl-line">
                  <button type="button" className="plan-line-main" onClick={() => setEditing(it.id)}><span className="plan-name">{it.name}</span></button>
                  <span className="plan-amount"><span className="money">{money(it.amount, it.currency)}</span></span>
                </li>
              ))}
            </ul>
            <div className="plan-tools">
              <button type="button" className="btn btn-ghost btn-sm add-line" onClick={() => setEditing(`new-${section}`)}><Icon name="plus" size={16} /> Добавить строку</button>
              {rows.length > 1 ? <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setEditing(null); setOrdering(section); }}><Icon name="grip" size={16} /> Порядок</button> : null}
            </div>
            </>}
            {editing === `new-${section}` ? (
              <LineModal section={section} onClose={() => setEditing(null)}
                onSubmit={(d) => wrap(post('/period-templates', { ...d, section, slot: Number(slot) }).then(reload))} />
            ) : null}
            {typeof editing === 'number' && rows.some((r) => r.id === editing) ? (() => {
              const it = rows.find((r) => r.id === editing)!;
              return (
                <LineModal key={it.id} section={section} initial={it} onClose={() => setEditing(null)}
                  onSubmit={(d) => wrap(patch(`/period-templates/${it.id}`, d).then(reload))}
                  onDelete={() => wrap(del(`/period-templates/${it.id}`).then(reload))} />
              );
            })() : null}
          </section>
        );
      })}
    </Modal>
  );
}
